import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTable, MatTableModule } from '@angular/material/table';
import { Router } from '@angular/router';
import { provideNgxMask } from 'ngx-mask';
import { SolicitacaoService } from 'src/app/core/apis/solicitacao/solicitacao.service';
import { TipoOrdem } from 'src/app/core/model/enum/tipo-ordem.enum';
import { Ordem } from 'src/app/core/model/ordem';
import { Titular } from 'src/app/core/model/titular';
import { DialogService } from 'src/app/core/service/dialog.service';
import { ErrorHandlerService } from 'src/app/core/service/error-handler.service';
import { OrdemBuilderService } from 'src/app/core/service/ordem-builder.service';
import { AcaoButtonsComponent } from 'src/app/shared/components/acao-buttons/acao-buttons.component';
import { ListaTitularesComponent } from "src/app/shared/components/lista-titulares/lista-titulares.component";
import { NroProcessoInputComponent } from 'src/app/shared/components/nro-processo-input/nro-processo-input.component';
import { SlaSelectorComponent } from 'src/app/shared/components/sla-selector/sla-selector.component';
import { NoSpecialCharsDirective } from 'src/app/shared/directive/no-special-chars.directive';
import { Globals } from 'src/app/shared/helpers/globals';
import { CustomValidators } from 'src/app/shared/validators/custom-validators';

@Component({
    selector: 'app-revogacao-ordem',
    templateUrl: './revogacao-ordem.component.html',
    styleUrls: ['./revogacao-ordem.component.css'],
    providers: [provideNgxMask()],
    imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatTableModule,
    MatMenuModule,
    MatSelectModule,
    MatOptionModule,
    MatProgressSpinnerModule,
    MatButtonModule,
    MatCheckboxModule,
    FormsModule,
    MatIconModule,
    NoSpecialCharsDirective,
    NroProcessoInputComponent,
    ListaTitularesComponent,
    SlaSelectorComponent,
    AcaoButtonsComponent
]
})
export class RevogacaoOrdemComponent {

  @Output() openBaixaTab = new EventEmitter<void>();

  @Input() tokenIam: string;

  form: UntypedFormGroup;

  @ViewChild(MatTable) table: MatTable<Titular>;
  @ViewChild(ListaTitularesComponent) listaTitularesComponent!: ListaTitularesComponent;

  displayedColumns: string[] = ["nome", "nroDocumento", "action"];

  titulares: Titular[];

  consultaAcaoRealizada: boolean = false;

  DESCRICAO_ORDEM = OrdemBuilderService.DESCRICAO_REVOGACAO;

  constructor(
    private globals: Globals,
    private solicitacaoService: SolicitacaoService,
    private ordemBuilderService: OrdemBuilderService,
    private dialogService: DialogService,
    private errorHandler: ErrorHandlerService,
    private router: Router,
    fb: UntypedFormBuilder
  ) {
    this.form = fb.group({
      nroProcesso: [""],
      nroSla: ["120", Validators.required],
      autor: ["", [Validators.required, CustomValidators.validatorOnlyNumber]],
      reu: ["", [Validators.required, CustomValidators.validatorOnlyNumber]]
    });

    this.titulares = [];
  }

  public buscarOrdem(){
    this.nroProcesso.disable();
    this.globals.addToLoading("cadastrar-ordem");
    this.solicitacaoService.findByNroProcesso(this.nroProcesso.value, [TipoOrdem.ACAO, TipoOrdem.BAIXA], this.tokenIam).subscribe({
        next: data => {
          let ordem: Ordem = data.body;
          if (ordem.status == "AGUARDANDO_ENVIO" || ordem.status == "EM_ATENDIMENTO") {
            this.globals.subtractFromLoading();
            this.dialogService.showOrdemEmAtendimentoDialog(ordem);
            this.nroProcesso.enable();
          } else if(ordem.tipoOrdem == TipoOrdem.ACAO) {
            this.globals.subtractFromLoading();
            this.openDialogBaixa(ordem);
          } else if(ordem.tipoOrdem == TipoOrdem.BAIXA) {
            this.globals.subtractFromLoading();
            this.openReincluirOrdemDialog();
          }
        },
        error: error => {
          this.globals.subtractFromLoading();

          if (error?.status === 404) {
            this.consultaAcaoRealizada = true;
            return;
          }

          this.nroProcesso.enable();
          this.globals.showSnackBar("Falha ao consultar ordens, por favor tente novamente mais tarde.");
        }
      }
    );
  }

  private touchForm() {
    this.form.markAllAsTouched();
    if(this.isTitularesEmpty()){
      this.listaTitularesComponent.nome.setErrors({titularesEmpty: true})
      this.listaTitularesComponent.documento.setErrors({titularesEmpty: true})
      this.listaTitularesComponent.form.markAllAsTouched();
    }
  }

  public isFormValid(): boolean{
    return  this.form.valid && !this.isTitularesEmpty();
  }

  private isTitularesEmpty(): boolean{
    return this.titulares.length == 0;
  }

  public openReincluirOrdemDialog(): void {
    this.dialogService.showConfirmation(
        ['Baixa da dívida encontrada na base de dados do Serasa.', 'Deseja reincluir esta dívida?'],
        'Reincluir',
        'Voltar',
        '400px'
      ).subscribe(confirm => {
        if (!confirm) {
          this.router.navigate(['ordem']);
        } else {
          this.consultaAcaoRealizada = true;
        }
      });
  }

  public onTitularesChange(titulares: Titular[]) {
    this.titulares = titulares;
  }

  public enviarRevogacao() {
    if(!this.isFormValid()){
      this.touchForm();
      return;
    }
    this.globals.addToLoading("cadastrar-ordem");

    const ordem = this.ordemBuilderService.buildRevogacao(this.nroProcesso.value, this.titulares, this.form.value);
    this.solicitacaoService.saveSolicitacao(ordem, this.tokenIam).subscribe({
      next: (response) => {
        this.globals.subtractFromLoading();
        this.globals.showSnackBar("Ordem de reinclusão enviada com sucesso!", ["snackbar-success"]);
        this.router.navigate(["/ordem"]);
      },
      error: (error) => {
        this.globals.subtractFromLoading();
        this.errorHandler.handle(error);
      }
    });
  }

  private openDialogBaixa(ordem: any): void {
    this.dialogService.showConfirmation(
        ['Encontramos ordem anterior cadastrada como inclusão para esse número de processo.', 'Deseja solicitar a baixa/exclusão?'],
        'Sim',
        'Não',
        '450px'
      ).subscribe(confirm => {
        if (confirm) {
          this.openBaixaTab.emit(ordem);
        } else {
          this.nroProcesso.enable();
        }
      });
  }

  get nroProcesso() {
    return this.form.get("nroProcesso");
  }

  get nroSla() {
    return this.form.get("nroSla");
  }

  get autor() {
    return this.form.get("autor");
  }

  get reu() {
    return this.form.get("reu");
  }

}
